import React from 'react';
import { Check, ArrowRight, Star, Users, BookOpen, Zap, Presentation, MapPin } from 'lucide-react';
import { PageTransition } from '../components/layout/PageTransition';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { DecorativeShape } from '../components/DecorativeShape';
import { Animate } from '../components/ui/Animate';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
export function Services() {
  const {
    t
  } = useLanguage();
  const services = [{
    id: 'essentials',
    icon: <Star size={32} data-id="element-815" />,
    color: 'bg-coral',
    lightColor: 'bg-coral/10',
    textColor: 'text-coral',
    tKey: 'essentials'
  }, {
    id: 'conflict',
    icon: <Users size={32} data-id="element-816" />,
    color: 'bg-teal',
    lightColor: 'bg-teal/10',
    textColor: 'text-teal',
    tKey: 'conflict'
  }, {
    id: 'wizardry',
    icon: <BookOpen size={32} data-id="element-817" />,
    color: 'bg-yellow-500',
    lightColor: 'bg-yellow-100',
    textColor: 'text-yellow-600',
    tKey: 'wizardry'
  }, {
    id: 'agile',
    icon: <Zap size={32} data-id="element-818" />,
    color: 'bg-blue-600',
    lightColor: 'bg-blue-50',
    textColor: 'text-blue-600',
    tKey: 'agile'
  }];
  const additionalServices = [{
    id: 'workshops',
    icon: <MapPin size={28} data-id="element-819" />,
    color: 'bg-coral',
    lightColor: 'bg-coral/10',
    textColor: 'text-coral',
    tKey: 'workshops',
    link: '/services/workshops'
  }, {
    id: 'talks',
    icon: <Presentation size={28} data-id="element-820" />,
    color: 'bg-teal',
    lightColor: 'bg-teal/10',
    textColor: 'text-teal',
    tKey: 'talks',
    link: '/services/talks'
  }, {
    id: 'courses',
    icon: <BookOpen size={28} data-id="element-821" />,
    color: 'bg-yellow-500',
    lightColor: 'bg-yellow-100',
    textColor: 'text-yellow-600',
    tKey: 'courses',
    link: '/services/courses'
  }];
  return <PageTransition data-id="element-822">
      <div className="pt-12 pb-24" data-id="element-823">
        <section className="text-center max-w-4xl mx-auto px-4 mb-20 relative" data-id="element-824">
          <DecorativeShape type="square" color="bg-teal/10" className="top-0 right-0 w-32 h-32 rotate-12 -z-10" data-id="element-825" />
          <h1 className="text-5xl md:text-6xl font-bold mb-6" data-id="element-826">
            {t('services.title')} <br data-id="element-827" />
            <span className="text-coral" data-id="element-828">{t('services.titleHighlight')}</span>
          </h1>
          <p className="text-xl text-gray-600" data-id="element-829">{t('services.subtitle')}</p>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12 mb-24" data-id="element-830">
          {services.map((service, index) => {
          const features = t(`services.items.${service.tKey}.features`) as string[];
          return <Animate key={service.id} delay={0} data-id="element-831">
                <Card className={`p-0 overflow-hidden border-2 ${index % 2 === 1 ? 'border-teal/20' : 'border-coral/20'}`} data-id="element-832">
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-0" data-id="element-833">
                    <div className={`lg:col-span-4 ${service.lightColor} p-10 flex flex-col justify-center items-center text-center relative overflow-hidden`} data-id="element-834">
                      <div className={`w-20 h-20 rounded-full ${service.color} text-white flex items-center justify-center mb-6 shadow-lg relative z-10`} data-id="element-835">
                        {service.icon}
                      </div>
                      <h3 className={`text-2xl font-bold ${service.textColor} mb-2 relative z-10`} data-id="element-836">
                        {t(`services.items.${service.tKey}.title`)}
                      </h3>
                      <p className="font-medium text-gray-600 relative z-10" data-id="element-837">
                        {t(`services.items.${service.tKey}.subtitle`)}
                      </p>
                    </div>
                    <div className="lg:col-span-8 p-8 md:p-12 flex flex-col justify-center bg-white" data-id="element-838">
                      <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-839">
                        {t(`services.items.${service.tKey}.description`)}
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10" data-id="element-840">
                        {features.map((feature: string, i: number) => <div key={i} className="flex items-start" data-id="element-841">
                            <div className={`mt-1 mr-3 p-1 rounded-full ${service.lightColor} ${service.textColor}`} data-id="element-842">
                              <Check size={14} strokeWidth={3} data-id="element-843" />
                            </div>
                            <span className="text-gray-700 font-medium" data-id="element-844">
                              {feature}
                            </span>
                          </div>)}
                      </div>
                      <div className="flex flex-col sm:flex-row items-center justify-end gap-6 pt-8 border-t border-gray-100" data-id="element-845">
                        <Link to="/contact" data-id="element-846">
                          <Button variant={index % 2 === 1 ? 'secondary' : 'primary'} rightIcon={<ArrowRight size={18} data-id="element-848" />} data-id="element-847">
                            {t('services.moreInfoCta')}
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </Card>
              </Animate>;
        })}
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-849">
          <Animate className="text-center mb-12" data-id="element-850">
            <h2 className="text-3xl font-bold text-charcoal mb-4" data-id="element-851">
              {t('services.moreOptions')}
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto" data-id="element-852">
              {t('services.moreOptionsSubtitle')}
            </p>
          </Animate>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-853">
            {additionalServices.map((service, index) => <Animate key={service.id} delay={index * 0.1} data-id="element-854">
                <Card className={`p-8 border-2 ${service.id === 'workshops' ? 'border-coral/20' : service.id === 'talks' ? 'border-teal/20' : 'border-yellow-200'} h-full`} data-id="element-855">
                  <div className="flex items-start gap-4 mb-4" data-id="element-856">
                    <div className={`p-3 rounded-2xl ${service.lightColor}`} data-id="element-857">
                      <div className={service.textColor} data-id="element-858">{service.icon}</div>
                    </div>
                    <div data-id="element-859">
                      <h3 className="text-xl font-bold text-charcoal" data-id="element-860">
                        {t(`services.additional.${service.tKey}.title`)}
                      </h3>
                    </div>
                  </div>
                  <p className="text-gray-600 mb-6" data-id="element-861">
                    {t(`services.additional.${service.tKey}.description`)}
                  </p>
                  <Link to={service.link} data-id="element-862">
                    <Button variant={service.id === 'workshops' ? 'primary' : service.id === 'talks' ? 'secondary' : 'outline'} rightIcon={<ArrowRight size={18} data-id="element-864" />} data-id="element-863">
                      {t('services.seeMore')}
                    </Button>
                  </Link>
                </Card>
              </Animate>)}
          </div>
        </section>
      </div>
    </PageTransition>;
}